import React, { useEffect, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';

import { rootUiActions } from 'app-components/flux/root-ui';
import { scheduledTransactionsActions } from 'app-components/flux/scheduled-transactions';
import { getLogger, tracker } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import { billPresentmentAPI } from 'app-components/flux/bill-presentment';
import { billPresentmentActions, billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import type { Biller, EnrollResponse } from 'app-components/flux/bill-presentment/billPresentmentTypes';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import { useStandardDrawerVariant } from 'components/Drawers/StandardDrawer';
import { getRootModalRemoveDialogOptions } from 'components/Drawers/standardDrawerContract';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import { IntroView, PendingView } from './HoldingScreen';
import LoginView from './LoginView';
import SearchView from './SearchView';
import { StepEnum } from './types';

import { showBillPresentmentDetails, type BillPresentmentProps } from './actions';

const log = getLogger('BillPresentment/billPresentmentDialog');

const BillPresentmentDialog = (props: BillPresentmentProps) => {
  const dispatch = useAppDispatch();

  const hasBillerAccounts = !!useAppSelector(billPresentmentSelectors.selectAllBillerAccounts)?.length;

  const { rootModalDrawer, series } = props;
  const isOverlayDrawer = useStandardDrawerVariant() === 'overlay';
  const reconnect = Boolean(series.presentmentAccountId && series.billerEnrollmentStatus === 'FAILED');

  const [GUID, setGUID] = useState<string>('');
  const [biller, setBiller] = useState<Biller | null>(null);
  const [enrollmentData, setEnrollmentData] = useState<EnrollResponse | null>(null);
  const initialStep = hasBillerAccounts ? (reconnect ? StepEnum.LOGIN : StepEnum.SEARCH) : StepEnum.INTRO;
  const [step, setStep] = useState<StepEnum>(
    rootModalDrawer && initialStep === StepEnum.LOGIN ? StepEnum.SEARCH : initialStep,
  );
  const [open, setOpen] = useState(true);

  const handleSelect = useCallback(
    async (biller: Biller) => {
      log.log('SELECTING: ', biller);
      if (biller?.id) {
        try {
          const response = await billPresentmentAPI.getEnrollment(biller?.id);
          log.debug('GET enroll response', response);
          const guid = uuidv4().toUpperCase();
          if (rootModalDrawer) {
            dispatch(
              showBillPresentmentDetails({
                biller,
                enrollment: response,
                guid,
                series,
              }),
            );
          } else {
            setBiller(biller);
            setEnrollmentData(response);
            setGUID(guid);
            setStep(StepEnum.LOGIN);
          }
        } catch (e) {
          log.warn('issue getting enrollment parameters', e);
        }
      }
      tracker.track(tracker.events.billerSelect, { billerName: biller?.name, type: biller?.activation });
    },
    [dispatch, rootModalDrawer, series],
  );

  const title = step === StepEnum.INTRO ? 'Bill Connect' : 'Connect biller';

  useEffect(() => {
    // auto-nav to login page if reconnect flow
    if (reconnect) {
      const state = store?.getState() as any;
      const biller = billPresentmentSelectors.selectBillerForAccountId(state, series?.presentmentAccountId || '');
      if (biller) handleSelect(biller);
    }
    // only check on first mount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setOpen(false);
    setStep(StepEnum.SEARCH);
    setEnrollmentData(null);
    dispatch(rootUiActions.removeDialog(`BPS-${series?.id}`, getRootModalRemoveDialogOptions(rootModalDrawer)));
  };

  const handleBack = () => {
    if (step === undefined) return;
    const sequence = [StepEnum.SEARCH, StepEnum.LOGIN];
    const currentIndex = sequence.indexOf(step);
    if (currentIndex > 0) {
      const previousStep = sequence[currentIndex - 1];
      setStep(previousStep);
    }
  };
  const isNestedDrawer = Boolean(rootModalDrawer && isOverlayDrawer);
  const canNavigateBackWithinFlow = step === StepEnum.LOGIN;
  const showDrawerBackButton = isNestedDrawer || canNavigateBackWithinFlow;
  const handleDrawerBack = () => {
    if (canNavigateBackWithinFlow) {
      handleBack();
      return;
    }

    handleClose();
  };

  let pageToRender;
  let action = null;
  switch (step) {
    case StepEnum.INTRO:
      pageToRender = <IntroView />;
      action = (
        <Button id='bill-connect-continue' variant='contained' onClick={() => setStep(StepEnum.SEARCH)}>
          Got it
        </Button>
      );
      break;
    case StepEnum.SEARCH:
      pageToRender = <SearchView handleSelect={handleSelect} />;
      break;
    case StepEnum.LOGIN:
      pageToRender = (
        <LoginView
          key={GUID} // GUID is set when enrollData returns, so this forces form re-init on new enrollment info
          enrollment={enrollmentData}
          biller={biller}
          setSteps={setStep}
          GUID={GUID}
          series={series}
          isDrawer={Boolean(rootModalDrawer)}
        />
      );
      break;
    case StepEnum.PENDING:
      pageToRender = <PendingView />;
      action = (
        <Button
          id='bill-connect-finish'
          variant='outlined'
          onClick={() => {
            // wait half a second and then fetch updated billerAccount and model (billers not hooked up to bump server)
            setTimeout(() => {
              dispatch(billPresentmentActions.fetch());
              dispatch(scheduledTransactionsActions.getScheduledTransactions());
            }, 500);
            handleClose();
          }}
        >
          Close
        </Button>
      );
      break;
    // complete may never be needed
    // case StepEnum.COMPLETE:
    //   pageToRender = <CompleteView />;
    //   break;
    default:
      pageToRender = null;
      break;
  }

  if (rootModalDrawer) {
    return (
      <Box
        aria-label={title}
        role='dialog'
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%' }}
      >
        <StdDialogTitle
          variant='drawer'
          id='dialog-title'
          onBack={showDrawerBackButton ? handleDrawerBack : undefined}
          onClose={handleClose}
          showBackButton={showDrawerBackButton}
          showCloseButton
        >
          {title}
        </StdDialogTitle>
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: step === StepEnum.LOGIN ? 'hidden' : 'auto',
          }}
        >
          {pageToRender}
        </Box>
        {action && <DialogActions>{action}</DialogActions>}
      </Box>
    );
  }

  return (
    <StdDialog
      open={open}
      onBack={handleBack}
      onClose={handleClose}
      dialogId={`BPS-${series?.id}`}
      sharedcomponentid='BILL_PRESENTMENT_DIALOG'
      showBackButton={canNavigateBackWithinFlow}
      size='md'
      title={title}
    >
      {pageToRender}
      {action && <DialogActions>{action}</DialogActions>}
    </StdDialog>
  );
};

export default BillPresentmentDialog;
